import React, { useEffect, useState } from "react";
import { api, type ProviderGoldenComparisonView, type ProviderGoldenReadinessView } from "../api";
import { ActionButton, StateMessage, StatusBadge } from "../design-system";
import { recordLabel, recordStatus, type AgentOsStudioRecord } from "./agentOsStudioModel";

export function HarnessProviderInspector({ harnesses, harnessUsage, providers, runs }: { harnesses: AgentOsStudioRecord[]; harnessUsage: AgentOsStudioRecord[]; providers: AgentOsStudioRecord[]; runs: AgentOsStudioRecord[] }): JSX.Element {
  const [comparison, setComparison] = useState<ProviderGoldenComparisonView | null>(null);
  const [readiness, setReadiness] = useState<ProviderGoldenReadinessView | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void api.providerGoldenComparisonReadiness()
      .then((value) => { if (active) setReadiness(value); })
      .catch((cause: unknown) => { if (active) setError(cause instanceof Error ? cause.message : "Provider-Readiness nicht verfügbar"); });
    return () => { active = false; };
  }, []);

  const executeComparison = async () => {
    setPending(true);
    setError(null);
    try {
      const executed = await api.providerGoldenComparisonExecute();
      setComparison(executed.comparison);
    } catch {
      setError("Der feste Provider-Lauf wurde fail-closed beendet. Kein Ergebnis wurde als PASS gewertet; Details stehen im Server-Audit.");
    } finally {
      setPending(false);
    }
  };

  const refreshComparison = async () => {
    if (!comparison) return;
    setPending(true);
    setError(null);
    try {
      const readback = await api.providerGoldenComparison(comparison.parentRunId);
      setComparison(readback.comparison);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Provider-Readback fehlgeschlagen");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="space-y-2" data-harness-provider-inspector>
      <section className="rounded-lg border border-white/10 bg-black/15 p-3" aria-label="Provider Golden Task Vergleich">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-jarvis-text">Codex / Claude Golden Task</p>
            <p className="mt-1 max-w-3xl text-xs leading-relaxed text-jarvis-muted">Ein fester Read-only-Auftrag läuft real über Codex und Claude im gemeinsamen Harness. PASS entsteht erst aus terminalen, attestierten Protokollrevisionen mit beobachteter Modellidentität und identischem normalisiertem Ergebnis-Hash.</p>
            <div className="mt-2 flex flex-wrap gap-2" aria-label="Provider Ausführungsbereitschaft">
              <StatusBadge status={readiness?.providers.codex ? "ready" : "blocked"} label={`Codex ${readiness?.providers.codex ? "bereit" : "nicht bereit"}`} />
              <StatusBadge status={readiness?.providers.claude ? "ready" : "blocked"} label={`Claude ${readiness?.providers.claude ? "bereit" : "nicht bereit"}`} />
              <span className="self-center text-[10px] text-jarvis-muted">Fester Task · keine freien Prompts · keine Produkt-Writes</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <ActionButton onClick={() => void executeComparison()} disabled={pending || readiness?.ready !== true}>{pending ? "Provider laufen…" : readiness ? "Golden Task ausführen" : "Readiness wird geprüft…"}</ActionButton>
            {comparison && <ActionButton variant="neutral" onClick={() => void refreshComparison()} disabled={pending}>Readback aktualisieren</ActionButton>}
          </div>
        </div>
        {error && <div className="mt-3"><StateMessage kind="error" label="Provider-Vergleich fehlgeschlagen">{error}</StateMessage></div>}
        {comparison && (
          <div className="mt-3 space-y-3" data-provider-golden-comparison>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={comparison.status === "pass" ? "ready" : comparison.status === "running" ? "pending" : "blocked"} label={comparison.status === "pass" ? "Parität bestätigt" : comparison.status === "running" ? "Provider-Runs warten" : comparison.status === "mismatch" ? "Ergebnisse weichen ab" : "Provider-Lauf fehlgeschlagen"} />
              <span className="text-[10px] text-jarvis-muted">{comparison.parentRunId}</span>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              {comparison.traces.map((trace) => (
                <article key={trace.runId} className="rounded-lg border border-white/10 bg-black/20 p-3">
                  <div className="flex justify-between gap-2"><span className="text-xs font-medium uppercase text-jarvis-text">{trace.actor}</span><span className="text-[10px] text-jarvis-muted">{trace.status}</span></div>
                  <dl className="mt-2 grid gap-1 text-[10px] text-jarvis-muted">
                    <div><dt className="inline">Modell: </dt><dd className="inline break-all">{trace.modelRef ?? "noch nicht gemeldet"}</dd></div>
                    <div><dt className="inline">Protokollrevision: </dt><dd className="inline">{trace.protocolRevision}</dd></div>
                    <div><dt className="inline">Ledger Events: </dt><dd className="inline">{trace.ledgerEventCount}</dd></div>
                    <div><dt className="inline">Resultat: </dt><dd className="inline break-all">{trace.resultSha256 ?? "ausstehend"}</dd></div>
                    <div><dt className="inline">Harness: </dt><dd className="inline break-all">{trace.protocolRef}</dd></div>
                    <div><dt className="inline">Brain-Packet: </dt><dd className="inline break-all">{trace.brainContextPacketRef}</dd></div>
                    <div><dt className="inline">Capability: </dt><dd className="inline break-all">{trace.capabilityEnvelopeRef}</dd></div>
                    <div><dt className="inline">Agent-OS: </dt><dd className="inline break-all">{trace.agentOsAttestationHash ?? "nicht attestiert"}</dd></div>
                  </dl>
                </article>
              ))}
            </div>
          </div>
        )}
      </section>
      <div className="grid gap-2 md:grid-cols-3">
        {[...harnesses, ...providers].slice(0, 12).map((item, index) => <article key={`${recordLabel(item)}-${index}`} className="rounded-lg border border-white/10 bg-black/15 p-3"><div className="flex justify-between gap-2"><span className="truncate text-xs text-jarvis-text">{recordLabel(item)}</span><span className="text-[10px] text-jarvis-muted">{recordStatus(item)}</span></div><p className="mt-1 text-[10px] text-jarvis-muted">{typeof item.source === "string" ? item.source : "Read-only Harness/Provider"}</p></article>)}
      </div>
      <div className="grid gap-2 md:grid-cols-2" aria-label="Harness Actor Usage Matrix">
        {harnessUsage.map((item, index) => <article key={`${recordLabel(item)}-${index}`} className="rounded-lg border border-white/10 bg-black/15 p-3"><div className="flex justify-between gap-2"><span className="truncate text-xs text-jarvis-text">{recordLabel(item)}</span><span className="text-[10px] text-jarvis-muted">{recordStatus(item)}</span></div><p className="mt-1 text-[10px] text-jarvis-muted">{Number(item.observedRuns ?? 0)} beobachtete Harness-Runs · Brain-Packet {item.brainPacketIssued === true ? "ausgestellt" : "nicht beobachtet"}</p></article>)}
        {harnessUsage.length === 0 && <p className="rounded-lg border border-white/10 p-3 text-xs text-jarvis-muted">Keine Harness-Akteur-Readbacks vorhanden.</p>}
      </div>
      <p className="text-[11px] text-jarvis-muted">{runs.length} Runs im Readback; Provider, Model, Policy, Evidence und Redaction werden nur aus echten Run-/Harness-Daten abgeleitet.</p>
    </div>
  );
}
